import BigButton from "components/atoms/buttons/BigButton";
import ToggleComponent from "components/atoms/toggle/Toggle";
import { InputNumber } from "antd";
import { FaRegHardDrive } from "react-icons/fa6";
import * as s from "../UsageSignupStyled";
import { UsageSignupFlow } from "../useUsageSignupFlow";
import {
  formatPrice,
  getPrepaidTotal,
  getYearDiscount,
  isYearlyBillingCycle,
} from "utils/priceUtils";

interface Props {
  flow: UsageSignupFlow;
}

export default function CloudStorageStep(props: Props): JSX.Element {
  const { flow } = props;
  const { paymentData, paymentPrice, termYears } = flow.state;
  const isYearly = isYearlyBillingCycle(paymentData.cycle);
  const isStorageEnabled = paymentData.storage > 0;
  const storageAmount = isStorageEnabled ? paymentData.storage : 1;
  const prepaidTotal = getPrepaidTotal(
    storageAmount,
    paymentPrice.storagePrice,
    termYears
  );
  const yearDiscount = getYearDiscount(paymentPrice.year, termYears);

  const handleToggleStorage = (checked: boolean) => {
    flow.actions.onChangeStorage(checked ? 1 : 0);
  };

  return (
    <>
      <s.CenterStage>
        <div className="icon storage">
          <FaRegHardDrive />
        </div>
        <h2>Cloud Storage</h2>
        <p>
          {isYearly
            ? "Add secure cloud storage for your organization's video recordings and data."
            : "Once signed up, you'll be able to add cloud storage to each individual end user. You'll also be able to add new features as they become available."}
        </p>
      </s.CenterStage>

      {isYearly && (
        <>
          <s.ToggleCard>
            <div className="copy">
              <h3>Add Cloud Storage</h3>
              <p>Optional — you can add it later too</p>
            </div>
            <ToggleComponent
              isToggled={isStorageEnabled}
              checked={isStorageEnabled}
              onChange={handleToggleStorage}
              dataTestId="usage-signup-cloud-storage-toggle"
            />
          </s.ToggleCard>

          {isStorageEnabled && (
            <>
              <s.SectionLabel>Billing Cycle</s.SectionLabel>
              <s.CycleGrid className="single">
                <s.CycleCard $active={true}>
                  <div className="header-row">
                    <div>
                      <div className="title-row">
                        <h3>Yearly</h3>
                        <span className="discount">
                          Save {yearDiscount}%
                        </span>
                      </div>
                      <strong>
                        ${formatPrice(paymentPrice.storagePrice)}/TB/yr
                      </strong>
                    </div>
                  </div>

                  <div className="embedded-field-row">
                    <div className="field-copy">
                      <label>Storage Amount</label>
                      <p>Choose how much cloud storage to prepay.</p>
                    </div>

                    <div className="field-control">
                      <InputNumber
                        min={1}
                        max={99}
                        value={storageAmount}
                        controls
                        onChange={flow.actions.onChangeStorage}
                      />
                      <span>TB</span>
                    </div>
                  </div>
                </s.CycleCard>
              </s.CycleGrid>

              <s.PriceCard>
                <div className="copy">
                  <h3>Storage Cost</h3>
                  <p>
                    {storageAmount} TB x $
                    {formatPrice(paymentPrice.storagePrice)}
                    /yr
                    {termYears > 1 ? ` x ${termYears} years` : ""}
                  </p>
                </div>
                <div className="price">
                  <strong>${formatPrice(prepaidTotal)}</strong>
                  <span>{termYears > 1 ? `/${termYears} years` : "/yr"}</span>
                </div>
              </s.PriceCard>
            </>
          )}
        </>
      )}

      <s.ButtonRow>
        <BigButton
          label="Back"
          buttonType="cancel"
          onClickBtn={() => flow.actions.onChangePage("previous")}
        />
        <BigButton
          label={isYearly ? "Review Order" : "Next"}
          onClickBtn={() => flow.actions.onChangePage("next")}
        />
      </s.ButtonRow>
    </>
  );
}
